// Cache the formatter for better performance
const amsterdamFormatter = new Intl.DateTimeFormat('en-US', {
    timeZone: 'Europe/Amsterdam',
    year: 'numeric',
    month: 'numeric',
    day: 'numeric',
    hour: 'numeric',
    minute: 'numeric',
    second: 'numeric',
    hour12: false,
});

/**
 * Converts a date string without timezone information to a Date object,
 * treating the time as CET (Central European Time - Europe/Amsterdam)
 */
const parseDateAsCET = (dateString: string): Date => {
    // Treat the input string as UTC by appending 'Z'
    // This allows native parsing and handles optional milliseconds automatically
    const asUtc = new Date(`${dateString}Z`);

    // Determine what this UTC time looks like in Amsterdam
    const parts = amsterdamFormatter.formatToParts(asUtc);

    // Helper to extract values from the parts array
    const getPart = (type: string) =>
        parseInt(parts.find((p) => p.type === type)?.value || '0', 10);

    // Create a UTC date that matches the Amsterdam components
    const amsterdamOffsetDate = new Date(
        Date.UTC(
            getPart('year'),
            getPart('month') - 1,
            getPart('day'),
            getPart('hour'),
            getPart('minute'),
            getPart('second'),
            asUtc.getUTCMilliseconds(), // Preserve milliseconds from original
        ),
    );

    // Calculate the offset and shift the original date
    // offset = (Amsterdam Time) - (UTC Time)
    const offsetMs = amsterdamOffsetDate.getTime() - asUtc.getTime();

    // We want the result to be: Input Time - Offset
    return new Date(asUtc.getTime() - offsetMs);
};

const createDateFromApiString = (dateString: string): Date => {
    // Check if the date string already has timezone information
    // Timezone info is indicated by 'Z' suffix or '+'/'-' with offset
    // Supports formats like: Z, +01:00, -05:00, +0100, -0500, +1:00
    const hasTimezone = /Z|[+-]\d{1,2}:?\d{2}$|[+-]\d{1,2}$/.test(dateString);

    if (!hasTimezone) {
        // If no timezone specified, treat as CET (Europe/Amsterdam)
        return parseDateAsCET(dateString);
    } else {
        return new Date(dateString);
    }
};

const calculateDifferenceInDays = (date1: Date, date2: Date): number => {
    return Math.abs((date2.getTime() - date1.getTime()) / (1000 * 3600 * 24));
};

const hasDatePassed = (date: Date | string) => {
    if (typeof date === 'string') date = createDateFromApiString(date);
    return Date.now() > date.getTime();
};

const betweenDates = (dateStart: Date, dateBetween: Date, dateEnd: Date): boolean => {
    const afterDateStart = dateBetween > dateStart;
    const beforeDateEnd = dateBetween < dateEnd;

    return afterDateStart && beforeDateEnd;
};

export { createDateFromApiString, calculateDifferenceInDays, hasDatePassed, betweenDates };
